import { PaytableSection }        from '../paytableSection.class'
import { LANG_TEXTS }             from '../../../../constants/lang/lang-texts.constant'
import { DEFAULT_PAYOUT_COLOURS } from '../../../../constants/defaultPayoutColours/default-payout-colours.constant'
import { BET_VALUES } from '../../../../constants/bet-values/bet-values.constant'

/**
 * @file Defines the {@link RulesManager} class.
 * @author Joe Thompson <joe.thompson@1x2network.com>
 */

 /** Class for handling payouts in paytable */
 export class PayoutManager extends PaytableSection {

  /**
   * Reference to payout content node
   * @memberof PayoutManager
   * @protected
   * @instance
   * @type {HTMLElement}
   */
  _body

  /**
   * Object mapping colours for sections of payout
   * @memberof PayoutManager
   * @protected
   * @instance
   * @type {object}
   */
  _colours
   
  /**
   * Currency symbol to display, if any
   */
  _currencySymbol
     
  /**
   * Amount of lines in the game
   */
  _lines
     
  /**
   * Format the payout values as currency
   */
  _showAsCurrency
     
   /**
    * Initialize payout manager
    * @param {object}      config_ - Payout section config
    * @param {HTMLElement} page_   - Payout page node
    */ 
   constructor(config_, page_) {

      super(config_, page_) //Call parent constructor
       
      this._showAsCurrency = this.shouldShowAsCurrency()
      this._currencySymbol = this._showAsCurrency ? balanceManager_.getCurrencySmb() || '' : ''
      this._lines = framework.getSetup().getPaylines() ? framework.getSetup().getPaylines().length : framework.getSetup().getPaylinesNum()

      // Update the payout values when the bet value changes
      BET_VALUES.changed.add(() => this.update())

      //Create div to store payout header and add to page
      let header = document.createElement('div')
      header.classList.add('paytable-header') //TODO: payout translation
      header.innerHTML = Translate.do('Payout')
      page_.appendChild(header)

      //Create div to store payout description and add to page
      let payoutDescDiv = document.createElement('div')
      payoutDescDiv.classList.add('payout-desc')
      ReelConfig.winningcombinations ? payoutDescDiv.innerHTML = Translate.do(LANG_TEXTS['winways']) : payoutDescDiv.innerHTML = Translate.do(LANG_TEXTS['winlines'])
      page_.appendChild(payoutDescDiv)

      //Create div to store payout content
      this._body     = document.createElement('div')
      this._body.id  = 'payout-content'

      this._colours = Object.create(null) //Initialize colours object

      //If colours config has been defined
      if (config_[0].symbols.colours) {

        //Loop through colour config
        for (let c in DEFAULT_PAYOUT_COLOURS) {
          config_[0].symbols.colours[c] ? this._colours[c] = config_[0].symbols.colours[c] : this._colours[c] = DEFAULT_PAYOUT_COLOURS[c] //Build colour config
        }

      }

      //Colours config not been defined
      else {
        this._colours = DEFAULT_PAYOUT_COLOURS //Use default colours
      }

      //Loop through payout config
      for (let i = 0; i < config_[0].symbols.config.length; i++) {

        let row = config_[0].symbols.config[i] //Store individual symbol row config
        let rowDiv = document.createElement('div')
        rowDiv.classList.add('payout-row')

        //Loop through payout rows
        for (let j = 0; j < row.length; j++) {

          let columns = row.length                                   //Calculate number of columns per row
          let content = row[j]                                       //Store content to be displayed in column
          rowDiv.appendChild(this.content(content, columns, rowDiv)) //Add content to row

        }

        this._body.appendChild(rowDiv) //Add row to content

      }

      page_.appendChild(this._body) //Add body to page

   }

   /**
    * Generates content for each column
    * @param {object} content_ - Content config
    * @param {number} columns_ - Number of columns in this row
    * @return {HTMLDivElement} Column div with content
    */
   content(content_, columns_) {

    //Create div to store column content
    let colDiv = document.createElement('div')
    colDiv.classList.add('payout-col')
    colDiv.style.width = `${100 / columns_}%`

    //If content ID is specified
    if (content_.id) {
      colDiv.id = content_.id //Set div ID
    }

    //If content has an image
    if (content_.bg) {
      content_.size ? colDiv.appendChild(this.image(content_.bg, content_.size)) : colDiv.appendChild(this.image(content_.bg)) //Generate image
    }

    //If content contains text
    if (content_.text) {
      
      //Loop through array of texts
      for (let i = 0; i < content_.text.length; i++) {
        colDiv.appendChild(this.text(content_.text[i])) //Generate text(s)
      }

    }

    //If content has payout values
    if (content_.payout_values) {

      let id = parseInt(content_.id.replace('symbol_', '')) //Store symbol ID
      colDiv.appendChild(this.values(id)) //Generate payout values

    }

    return colDiv //Return generated column content

   }

   /**
    * Generates an image in payout
    * @param {string} url_           - URL of asset to load in image
    * @param {object} [size_ = null] - Size of image
    * @return {HTMLImageElement} Img div to add
    */
   image(url_, size_ = null) {

    //Create img to load image
    let imgDiv = document.createElement('img')
    imgDiv.classList.add('payout-col-img')
    imgDiv.src = url_

    //If config defines image size
    if (size_) {

      //Set image size
      imgDiv.style.height = `${size_.height}px`
      imgDiv.style.width  = `${size_.width}px`

    }

    return imgDiv //Return image to add to column

   }

   /**
    * Generates text in payout
    * @param {string[]} t_ - Texts to be added
    * @return {HTMLSpanElement} span to add
    */
   text(t_) {

    //Create span to store text
    let span         = document.createElement('span')
    span.innerHTML   = Translate.do(t_)
    span.style.color = this._colours.text

    return span //Return span containing text to column

   }
     
   /**
    * The paytable should show the currency symbol for 88 RTP games in Germany
    * @returns {boolean} - Whether or not the currency should be shown
    */
   shouldShowAsCurrency() {
    const gameID = String(gameParams.gameID)
    const is88RTP = gameID.startsWith('72') || gameID.startsWith('75')
    const isGermany = loginVars.getRegParams().jurisdiction.toLowerCase() === 'de'
    return is88RTP && isGermany
   }

   /**
    * Retrieves payout values for a symbol
    * @param {number} id_ - Symbol ID
    * @return {HTMLDivElement} Div containing payout values
    */
   values(id_) {
    
    let values = framework.getSetup().getSymbolValues(id_) //Store payout values for symbol
    const stake = BET_VALUES._values && BET_VALUES.value()

    //Create div to store payout values
    let valuesDiv = document.createElement('div')
    valuesDiv.classList.add('payout-col-values')

    //Loop through payout values
    for (let v in values) {

      let valuesRowDiv = document.createElement('div') //Create div to store a row mapping payout amount & quantity

      //Create span to store payout quantity/matched
      let matchedDiv = document.createElement('span')
      matchedDiv.innerHTML = `${v}:`
      matchedDiv.style.color = this._colours.matched
      valuesRowDiv.appendChild(matchedDiv)
        
      // Format the payout amount
      const amount = (stake && this._showAsCurrency) ? ((values[v] / this._lines) * stake).toFixed(2) : values[v]

      //Create span to store payout amount
      let amtDiv = document.createElement('span')
      amtDiv.classList.add('payout-text')
      amtDiv.innerHTML = this._currencySymbol + amount
      amtDiv.style.color = this._colours.amount
      valuesRowDiv.appendChild(amtDiv)

      valuesDiv.appendChild(valuesRowDiv) //Add row mapping payout amount & quantity to payout values div

    }

    return valuesDiv //Return payout values to column

   }
     
   /**
    * Update the payout values with the current stake
    */
   update() {
    if (!this._showAsCurrency) return   
    
    const config = this._config
    const stake = BET_VALUES.value() // Get the current stake
    
    // Loop through symbol rows
    for (let i = 0; i < config[0].symbols.config.length; i++) {
      const row = config[0].symbols.config[i] //Store individual symbol row config

      //Loop through payout rows
      for (let j = 0; j < row.length; j++) {
        const content = row[j] //Store content to be displayed in column
        if (!content.payout_values) continue

        const payoutValues = document.getElementById(content.id).querySelector('.payout-col-values') // Find payout values div
        const id = parseInt(content.id.replace('symbol_', '')) //Store symbol ID
        const values = framework.getSetup().getSymbolValues(id) //Store payout values for symbol
        
        // Update payout text in children elements
        const children = payoutValues.children
        let i = 0 // values isn't an array so we need to keep track of the index
        for (let v in values) {
          const child = children[i]
          const value = values[v]
          const textNode = child.querySelector('.payout-text') // Find the text node
          
          const amount = ((value / this._lines) * stake).toFixed(2) // Calculate the new amount from the current stake and payout value
          textNode.textContent = this._currencySymbol + amount // Update the text with the new amount and currency symbol
          i++
        }
      }

    }
    
  }

 }